import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatRut } from '@/utils';
import { ROUTE_DEFINITIONS } from '@/routes';
import { useTranslation } from '@/i18n';
import { colors } from '@/assets/styles/colors';
import { Text } from '@/components/ui/text/Text';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import { companyService, type Company } from '@/api/domains/company';
import { useUI } from '@/hooks/ui/useUI';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';


interface UseCompanyListResult {
  rows: Company[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<Company>[];
  actions: TableAction<Company>[];
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  loading: boolean;
}

export function useCompanyList(): UseCompanyListResult {
  const { showApiError } = useUI();
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const { canEdit } = usePagePermissions();


  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<Record<string, never>>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<Company> | null> => {
      const response = await companyService.list(
        {
          page: params.page,
          pageSize: params.pageSize,
          searchText: params.searchText,
          sort: params.sort
            ? { key: params.sort.key, direction: params.sort.direction.toLowerCase() as 'asc' | 'desc' }
            : undefined,
        },
        signal,
      );
      if (!response.success || !response.data) {
        if (response.error?.code !== 'ABORTED') {
          showApiError(response);
        }
        return null;
      }
      return { items: response.data.data, totalRecords: response.data.totalRecords };
    },
    [showApiError],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
  } = usePaginatedList<Company, Record<string, never>>({
    fetchFn,
    initialFilters: {} as Record<string, never>,
    initialSort: { key: 'companyName', direction: 'ASC' },
    orderByMap: {
      id: 'companyId',
      name: 'companyName',
      rut: 'companyRUT',
      type: 'companyType',
      syncWithRex: 'syncWithRex',
    },
    deduplicate: true,
    screenCode: ROUTE_DEFINITIONS.EMPRESAS.code,
  });

  const columns: TableColumn<Company>[] = useMemo(
    () => [
      {
        key: 'id',
        header: 'ID',
        sortable: true,
        render: (row: Company) => row.id,
      },
      {
        key: 'rut',
        header: 'RUT',
        sortable: true,
        render: (row: Company) => formatRut(row.rut),
      },
      {
        key: 'name',
        header: 'Razón Social',
        sortable: true,
        maxWidth: 250,
        render: (row: Company) => (
          <Text
            variant="subheader-medium"
            color={colors.important.main}
            as="span"
            onClick={() => {
              void navigate(ROUTE_DEFINITIONS.EMPRESAS_PERFIL.path.replace(':companyId', String(row.id)));
            }}
            className="cursor-pointer transition-colors hover:opacity-80"
          >
            {row.name}
          </Text>
        ),
      },
      {
        key: 'syncWithRex',
        header: 'Sincroniza con Rex+',
        sortable: true,
        render: (row: Company) => (
          <Text variant="body-regular">{row.syncWithRex ? 'Sí' : 'No'}</Text>
        ),
      },
      {
        key: 'type',
        header: 'Tipo',
        sortable: true,
        render: (row: Company) => row.type,
      },
    ],
    [navigate],
  );

  const actions: TableAction<Company>[] = useMemo(
    () => [
      {
        id: 'edit',
        label: translate('common:tableActions.edit'),
        icon: 'edit',
        hidden: () => !canEdit,
        onClick: (row: Company) => {
          void navigate(
            ROUTE_DEFINITIONS.EMPRESAS_PERFIL.path.replace(':companyId', String(row.id)),
          );
        },
      },
    ],
    [translate, canEdit, navigate],
  );

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    onSearch,
    onSortChange,
    loading,
  };
}

export default useCompanyList;
